<template>
  <b-modal
    id="back-to-home-user-modal"
    ref="modal"
    :title="isEdit ? 'Edit VPN User' : 'Add VPN User'"
    hide-footer
    centered
    @show="fillForm"
  >
    <validation-observer ref="observer">
      <b-form @submit.prevent="submit">
        <b-form-group
          label="Name:"
          label-class="label-class"
        >
          <validation-provider
            #default="{ errors }"
            name="Name"
            vid="name"
            rules="required|max:64"
          >
            <b-form-input
              v-model="form.name"
              :state="errors.length > 0 ? false : null"
              placeholder="Name"
              autocomplete="off"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group
          label="Comment:"
          label-class="label-class"
        >
          <validation-provider
            #default="{ errors }"
            name="Comment"
            vid="comment"
            rules="max:255"
          >
            <b-form-input
              v-model="form.comment"
              :state="errors.length > 0 ? false : null"
              placeholder="Comment"
              autocomplete="off"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group
          label="LAN access:"
          label-class="label-class"
          description="Without LAN access the user reaches only the internet through this site."
        >
          <b-form-checkbox
            v-model="form.allow_lan"
            switch
            inline
          />
        </b-form-group>
        <template v-if="!isEdit">
          <b-form-group
            label="Enabled:"
            label-class="label-class"
          >
            <b-form-checkbox
              v-model="form.is_enabled"
              switch
              inline
            />
          </b-form-group>
          <b-form-group
            label="Expires:"
            label-class="label-class"
            description="Leave empty for a user that never expires. The date cannot be changed later."
          >
            <validation-provider
              #default="{ errors }"
              name="Expires"
              vid="expires_at"
            >
              <b-form-datepicker
                v-model="form.expires_date"
                :min="minExpiryDate"
                locale="en-GB"
                placeholder="Never"
                reset-button
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </template>
        <template v-else>
          <p class="mb-50">
            <span class="card-label-bold">Expires:</span>
            {{ user.expires_at || 'Never' }}
          </p>
          <p>
            <span class="card-label-bold">Client address:</span>
            {{ user.client_addresses.join(', ') || '-' }}
          </p>
        </template>
        <div class="text-right">
          <b-button
            type="submit"
            variant="primary"
            :disabled="isSaving"
          >
            <b-spinner
              v-if="isSaving"
              small
              class="mr-50"
            />
            Save
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import {
  BModal, BForm, BFormGroup, BFormInput, BFormCheckbox, BFormDatepicker, BButton, BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { required, max } from '@validations'

const getDefaultForm = () => ({
  name: '',
  comment: '',
  allow_lan: true,
  is_enabled: true,
  expires_date: '',
})

export default {
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormDatepicker,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    siteId: {
      type: [String, Number],
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      max,
      form: getDefaultForm(),
      isSaving: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.user
    },
    minExpiryDate() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)

      return tomorrow
    },
  },
  methods: {
    ...mapActions('backToHome', ['createUser', 'updateUser']),
    show() {
      this.$refs.modal.show()
    },
    fillForm() {
      this.form = getDefaultForm()

      if (this.user) {
        this.form.name = this.user.name
        this.form.comment = this.user.comment || ''
        this.form.allow_lan = this.user.allow_lan
      }
    },
    async submit() {
      if (!await this.$refs.observer.validate()) {
        return
      }

      this.isSaving = true

      try {
        if (this.isEdit) {
          await this.updateUser({
            siteId: this.siteId,
            id: this.user.id,
            payload: {
              name: this.form.name,
              comment: this.form.comment,
              allow_lan: this.form.allow_lan,
            },
          })
        } else {
          await this.createUser({
            siteId: this.siteId,
            payload: {
              name: this.form.name,
              comment: this.form.comment,
              allow_lan: this.form.allow_lan,
              is_enabled: this.form.is_enabled,
              // The user can connect through the whole chosen day.
              expires_at: this.form.expires_date ? `${this.form.expires_date} 23:59:59` : null,
            },
          })
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.isEdit ? 'VPN user updated' : 'VPN user added',
            icon: 'BellIcon',
            variant: 'success',
          },
        })
        this.$refs.modal.hide()
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.$refs.observer.setErrors(error.response.data.fields || {})
        }
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>
